import { z } from 'zod';
import {
  resourceKeySchema,
  statusToneSchema,
  STATUS_TONES,
  timestampSchema,
  versionedSchema,
  type StatusTone,
} from './common';
import {
  mapLayerStyleSchema,
  styleValueSchema,
  STYLE_SLOT_KEYS,
  type MapLayerStyle,
  type StyleSlotKey,
} from './map-style';

/**
 * The spatial dimension.
 *
 * Most of what this product tracks happens *somewhere*: a track renewal runs
 * between two stations, a possession closes a named section, an edge host sits
 * in one building. Until now that fact was carried in prose -- a task called
 * "Pasila-Riihimaki" -- which a reader can follow and a computer cannot.
 *
 * Three decisions shape everything below, and the obvious alternatives are all
 * worse in a specific way:
 *
 * **Geometry lives in its own record, not as columns on the things it locates.**
 * A latitude/longitude pair on `service`, `schedule_task` and every future
 * mappable table is the same field defined five times, five read models to
 * teach about geometry, and no home at all for a feature that is *only* spatial
 * -- a site boundary, an access route, a protected area. One `map_feature` row
 * that optionally points at a domain row inverts that: the map is a module that
 * can be switched off, and one viewport query answers for every layer at once.
 *
 * **Coordinates are WGS84 longitude/latitude, in that order, always.** GeoJSON
 * (RFC 7946) fixes the order, MapLibre expects it, and every reversed-pair bug
 * anyone has ever shipped came from a codebase where the order depended on
 * which function you called. There is no second convention here to convert to.
 *
 * **The wire format is GeoJSON, not a bespoke shape.** The list endpoint returns
 * a FeatureCollection the browser hands to a map source unmodified. A custom
 * envelope would have to be unwrapped on arrival, and the only thing that
 * unwrapping could add is a chance to get it wrong.
 *
 * ## What is deliberately not here
 *
 * No coordinate reference system field, no projected coordinates, no
 * antimeridian handling. A single infrastructure project spans a corridor, not
 * a hemisphere; the envelope arithmetic below assumes a bounding box does not
 * cross the antimeridian, and a deployment that needs otherwise needs PostGIS
 * underneath it rather than a wider branch here.
 */

/* ------------------------------------------------------------------ layers */

/**
 * What a feature *is*, for the purpose of drawing and filtering it.
 *
 * A presentation grouping, deliberately separate from what the feature is
 * linked to. An `asset` is usually a service and a `work_area` is usually a
 * schedule task, but neither is required to be: an inventory of assets exists
 * before anything monitors them, and a work area is drawn during planning
 * before the task that will happen there is written down. Folding the two
 * together would mean the map could only show things that already had a row
 * somewhere else, which is precisely the state this module exists to fix.
 *
 * This used to be a five-member enum -- in Zod, in PostgreSQL, in five
 * translation keys and in five CSS rules. It is now a row in `map_layer`, and
 * what travels on the wire is that row's key: a client whose work is organised
 * around drainage and ecology can say so, instead of filing both under
 * `hazard` (docs/map-layers-design.md section 2.1).
 *
 * The key and not the id, because it is what a feature's properties carry into
 * a style filter, what an import names, and what a person reads in a URL. The
 * id is for referential integrity and stays in the database.
 */
export const mapLayerKeySchema = resourceKeySchema;
export type MapLayerKey = z.infer<typeof mapLayerKeySchema>;

/**
 * The three shapes a feature can take, named by what they mean rather than by
 * their GeoJSON type.
 *
 * The renderer needs this grouping and not the finer distinction: a Polygon and
 * a MultiPolygon are drawn by the same fill layer. Storing it saves every
 * consumer from re-deriving it, and computing it in one function is what stops
 * the stored value disagreeing with the geometry beside it.
 */
export const MAP_GEOMETRY_KINDS = ['point', 'line', 'area'] as const;
export const mapGeometryKindSchema = z.enum(MAP_GEOMETRY_KINDS);
export type MapGeometryKind = (typeof MAP_GEOMETRY_KINDS)[number];

/**
 * The severity colour a feature is drawn in.
 *
 * Aliases of the shared tone (contracts: common.ts) rather than a second copy
 * of the same five members. The map's names are kept because they are what the
 * renderer, the legend and every existing caller say, and because a tone here
 * is specifically the colour of a *feature*.
 */
export const MAP_STATUS_TONES = STATUS_TONES;
export const mapStatusToneSchema = statusToneSchema;
export type MapStatusTone = StatusTone;

/**
 * Where a map gets its features from.
 *
 * `features` is one GeoJSON document fetched once and then panned and zoomed
 * with no further requests. It is right at project scale -- a few hundred rows
 * along one corridor -- and it is the only mode with *exact* geometry: nothing
 * is simplified, clipped or dropped.
 *
 * `tiles` is the same features cut into MVT tiles and fetched as the reader
 * moves. It costs simplification at low zoom and a request per tile, and it
 * buys a map that does not have to stop at a limit.
 *
 * The server decides which, from the project's own size, and says so in the
 * overview. The browser could work it out too and must not: the threshold is a
 * property of what the API is willing to send in one response, and two places
 * holding the same number is two places for it to drift.
 */
export const MAP_SOURCE_MODES = ['features', 'tiles'] as const;
export const mapSourceModeSchema = z.enum(MAP_SOURCE_MODES);
export type MapSourceMode = (typeof MAP_SOURCE_MODES)[number];

/**
 * Domain records a feature may be pinned to. Closed, because each one needs a
 * resolver on the read path and a link target in the detail panel.
 */
export const MAP_SUBJECT_KINDS = ['infrastructure_asset', 'schedule_task'] as const;
export const mapSubjectKindSchema = z.enum(MAP_SUBJECT_KINDS);
export type MapSubjectKind = (typeof MAP_SUBJECT_KINDS)[number];

/* ---------------------------------------------------------------- geometry */

const longitudeSchema = z.number().min(-180).max(180);
const latitudeSchema = z.number().min(-90).max(90);

/**
 * A GeoJSON position: longitude first, then latitude.
 *
 * RFC 7946 also allows a third element for elevation. It is rejected here
 * rather than ignored: nothing in this product reads it, and silently accepting
 * a value nobody stores is how a caller comes to believe altitude round-trips.
 */
export const positionSchema = z.tuple([longitudeSchema, latitudeSchema]);
export type Position = z.infer<typeof positionSchema>;

/**
 * Vertex ceiling per geometry.
 *
 * Section 10.1 forbids unbounded payloads, and a hand-drawn area cannot reach
 * this while an imported survey trace with one point per metre passes it in the
 * first kilometre. Refusing at the boundary is kinder than accepting a row that
 * makes every later viewport read slow.
 */
export const MAX_GEOMETRY_POSITIONS = 5_000;

const positionListSchema = z.array(positionSchema).max(MAX_GEOMETRY_POSITIONS);

const pointGeometrySchema = z
  .object({ type: z.literal('Point'), coordinates: positionSchema })
  .strict();

const lineStringGeometrySchema = z
  .object({ type: z.literal('LineString'), coordinates: positionListSchema.min(2) })
  .strict();

/**
 * A polygon ring closes on itself: at least four positions, first equal to
 * last. Enforced rather than repaired, because a ring the caller did not close
 * is more likely a truncated payload than a stylistic choice, and quietly
 * appending the first point again would draw an edge nobody asked for.
 */
const linearRingSchema = positionListSchema.min(4).refine(isClosedRing, {
  message: 'a polygon ring must be closed: the last position must equal the first',
});

const polygonGeometrySchema = z
  .object({ type: z.literal('Polygon'), coordinates: z.array(linearRingSchema).min(1).max(32) })
  .strict();

const multiLineStringGeometrySchema = z
  .object({
    type: z.literal('MultiLineString'),
    coordinates: z.array(positionListSchema.min(2)).min(1).max(64),
  })
  .strict();

const multiPolygonGeometrySchema = z
  .object({
    type: z.literal('MultiPolygon'),
    coordinates: z.array(z.array(linearRingSchema).min(1).max(32)).min(1).max(64),
  })
  .strict();

/**
 * The geometries this product stores.
 *
 * MultiPoint and GeometryCollection are absent on purpose. A multi-point is
 * several features wearing one identity -- clicking it could only ever select
 * all of them -- and a geometry collection is the same problem without the
 * common type. Both are better modelled as several rows, which the data model
 * already supports and the map already draws.
 */
export const mapGeometrySchema = z.discriminatedUnion('type', [
  pointGeometrySchema,
  lineStringGeometrySchema,
  polygonGeometrySchema,
  multiLineStringGeometrySchema,
  multiPolygonGeometrySchema,
]);
export type MapGeometry = z.infer<typeof mapGeometrySchema>;

function isClosedRing(ring: readonly Position[]): boolean {
  const first = ring[0];
  const last = ring[ring.length - 1];
  return first !== undefined && last !== undefined && first[0] === last[0] && first[1] === last[1];
}

/**
 * Which drawing layer a geometry belongs to.
 */
export function geometryKindOf(geometry: MapGeometry): MapGeometryKind {
  switch (geometry.type) {
    case 'Point':
      return 'point';
    case 'LineString':
    case 'MultiLineString':
      return 'line';
    default:
      return 'area';
  }
}

/**
 * Every position in a geometry, flattened. Small by construction: the vertex
 * ceiling above is what keeps this from being an unbounded walk.
 */
export function geometryPositions(geometry: MapGeometry): Position[] {
  switch (geometry.type) {
    case 'Point':
      return [geometry.coordinates];
    case 'LineString':
      return [...geometry.coordinates];
    case 'MultiLineString':
      return geometry.coordinates.flat();
    case 'Polygon':
      return geometry.coordinates.flat();
    case 'MultiPolygon':
      return geometry.coordinates.flat(2);
  }
}

/**
 * An axis-aligned bounding box, ordered west, south, east, north.
 *
 * The GeoJSON `bbox` member order, so it can be assigned to one directly and so
 * a reader who knows the format does not have to check ours.
 */
export const boundingBoxSchema = z
  .tuple([longitudeSchema, latitudeSchema, longitudeSchema, latitudeSchema])
  .refine(([west, south, east, north]) => west <= east && south <= north, {
    message: 'bounding box must be ordered west, south, east, north',
  });
export type BoundingBox = z.infer<typeof boundingBoxSchema>;

/**
 * The envelope of a geometry.
 *
 * Stored denormalized on the row, which is the whole reason a viewport query is
 * four indexed comparisons instead of a JSON parse per candidate. It is
 * computed here, in the package every writer shares, so the stored envelope and
 * the geometry beside it cannot describe different shapes.
 */
export function computeBoundingBox(geometry: MapGeometry): BoundingBox {
  let west = Number.POSITIVE_INFINITY;
  let south = Number.POSITIVE_INFINITY;
  let east = Number.NEGATIVE_INFINITY;
  let north = Number.NEGATIVE_INFINITY;

  for (const [longitude, latitude] of geometryPositions(geometry)) {
    if (longitude < west) west = longitude;
    if (longitude > east) east = longitude;
    if (latitude < south) south = latitude;
    if (latitude > north) north = latitude;
  }

  return [west, south, east, north];
}

/** Union of two envelopes, for "fit the map to everything currently shown". */
export function unionBoundingBox(a: BoundingBox, b: BoundingBox): BoundingBox {
  return [Math.min(a[0], b[0]), Math.min(a[1], b[1]), Math.max(a[2], b[2]), Math.max(a[3], b[3])];
}

/** Whether two envelopes overlap. The viewport filter, in the one place the
 *  client and the server can both use it. */
export function boundingBoxesIntersect(a: BoundingBox, b: BoundingBox): boolean {
  return a[0] <= b[2] && a[2] >= b[0] && a[1] <= b[3] && a[3] >= b[1];
}

/**
 * A single point that stands for the whole feature: where its label is drawn,
 * where "fly to this" lands, and where it sits when the map clusters.
 *
 * Not the centre of the bounding box, which is the cheap answer and the wrong
 * one often enough to notice. The centre of a curved rail alignment's envelope
 * can be kilometres off the track, in a field, and a reader who clicks a search
 * result expects the map to move to the thing rather than to its neighbourhood.
 * So a line gets the point half-way along its length, and an area gets its true
 * centroid.
 */
export function representativePoint(geometry: MapGeometry): Position {
  switch (geometry.type) {
    case 'Point':
      return geometry.coordinates;
    case 'LineString':
      return midpointAlong(geometry.coordinates);
    case 'MultiLineString':
      return midpointAlong(largestBy(geometry.coordinates, lineLength));
    case 'Polygon':
      return ringCentroid(geometry.coordinates[0] ?? []) ?? boundingBoxCentre(geometry);
    case 'MultiPolygon': {
      const outerRings = geometry.coordinates.map((polygon) => polygon[0] ?? []);
      const largest = largestBy(outerRings, (ring) => Math.abs(signedRingArea(ring)));
      return ringCentroid(largest) ?? boundingBoxCentre(geometry);
    }
  }
}

function boundingBoxCentre(geometry: MapGeometry): Position {
  const [west, south, east, north] = computeBoundingBox(geometry);
  return [(west + east) / 2, (south + north) / 2];
}

function largestBy<T>(items: readonly T[], measure: (item: T) => number): T {
  let best = items[0] as T;
  let bestScore = Number.NEGATIVE_INFINITY;
  for (const item of items) {
    const score = measure(item);
    if (score > bestScore) {
      bestScore = score;
      best = item;
    }
  }
  return best;
}

/**
 * Planar length in degrees. Not metres, and it does not need to be: it is only
 * ever used to compare parts of the same geometry against each other, where the
 * latitude-dependent scale factor is common to both and cancels.
 */
function lineLength(line: readonly Position[]): number {
  let total = 0;
  for (let index = 1; index < line.length; index += 1) {
    total += segmentLength(line[index - 1] as Position, line[index] as Position);
  }
  return total;
}

function segmentLength(from: Position, to: Position): number {
  return Math.hypot(to[0] - from[0], to[1] - from[1]);
}

function midpointAlong(line: readonly Position[]): Position {
  if (line.length === 0) return [0, 0];
  if (line.length === 1) return line[0] as Position;

  const half = lineLength(line) / 2;
  // A zero-length line -- every vertex identical -- would divide by zero below.
  if (half === 0) return line[0] as Position;

  let travelled = 0;
  for (let index = 1; index < line.length; index += 1) {
    const from = line[index - 1] as Position;
    const to = line[index] as Position;
    const length = segmentLength(from, to);
    if (travelled + length >= half) {
      const ratio = length === 0 ? 0 : (half - travelled) / length;
      return [from[0] + (to[0] - from[0]) * ratio, from[1] + (to[1] - from[1]) * ratio];
    }
    travelled += length;
  }

  return line[line.length - 1] as Position;
}

/** Twice the signed area of a closed ring; positive when it winds anticlockwise. */
function signedRingArea(ring: readonly Position[]): number {
  let total = 0;
  for (let index = 0; index < ring.length - 1; index += 1) {
    const [x1, y1] = ring[index] as Position;
    const [x2, y2] = ring[index + 1] as Position;
    total += x1 * y2 - x2 * y1;
  }
  return total;
}

/**
 * The centroid of a closed ring, or null when the ring encloses no area -- a
 * degenerate ring whose vertices are collinear, which the formula would answer
 * with a division by zero. Callers fall back to the envelope centre, which for
 * a shape with no area is the honest answer anyway.
 */
function ringCentroid(ring: readonly Position[]): Position | null {
  const doubleArea = signedRingArea(ring);
  if (doubleArea === 0) return null;

  let x = 0;
  let y = 0;
  for (let index = 0; index < ring.length - 1; index += 1) {
    const [x1, y1] = ring[index] as Position;
    const [x2, y2] = ring[index + 1] as Position;
    const cross = x1 * y2 - x2 * y1;
    x += (x1 + x2) * cross;
    y += (y1 + y2) * cross;
  }

  return [x / (3 * doubleArea), y / (3 * doubleArea)];
}

/* ----------------------------------------------------------- wire: reading */

/**
 * The properties carried on every feature in the collection endpoint.
 *
 * Flat, and primitive-valued only. That is not a style preference: a GeoJSON
 * source's features cross a worker boundary inside the map library, and nested
 * property values come back from a hit test as JSON strings rather than as the
 * objects that went in. Code reading `feature.properties.subject.label` would
 * pass a unit test and produce undefined on a real click.
 *
 * It is also the payload bound. Everything a reader needs to *find* and
 * *recognise* a feature is here; everything they need to *study* one is behind
 * the detail endpoint, fetched when they select it. A collection of two thousand
 * features stays a list of short strings rather than every custom attribute
 * anyone ever attached.
 */
export const mapFeaturePropertiesSchema = z.object({
  id: z.string().uuid(),
  key: resourceKeySchema,
  name: z.string(),
  layer: mapLayerKeySchema,
  geometryKind: mapGeometryKindSchema,
  statusTone: mapStatusToneSchema,
  /**
   * Translation key suffix for the status shown in the hover card, or null when
   * the feature is not linked to anything that has a status. A code, never a
   * sentence: the backend does not speak the reader's language (section 16).
   */
  statusCode: z.string().nullable(),
  subjectKind: mapSubjectKindSchema.nullable(),
  subjectId: z.string().uuid().nullable(),
  subjectLabel: z.string().nullable(),
  /**
   * The styling slots, `v0`..`v7`.
   *
   * This is how a data-driven style gets its input without the payload bound
   * above becoming a lie. A style *declares* which fields it reads; the
   * collection resolves the union of those declarations and puts the values
   * here, and the envelope's `fields` array says which reference each slot
   * holds (docs/map-layers-design.md section 2.3).
   *
   * Numbered rather than named after the field, so the schema stays closed: a
   * catch-all would type every known property as `string | number | null` too,
   * and a property nobody meant to send would arrive without complaint.
   *
   * Absent, not null, when the request needed no such slot -- a map with no
   * data-driven layer on it sends none of these at all.
   */
  ...styleSlotShape(),
});
export type MapFeatureProperties = z.infer<typeof mapFeaturePropertiesSchema>;

/**
 * `v0`..`v7` as optional primitives.
 *
 * The return type is a mapped type over the slot keys rather than a
 * `Record<string, ...>`, and that is load-bearing: Zod infers the object's type
 * from the shape it is given, and a shape typed by a string index signature
 * infers away to nothing -- the slots disappear from `MapFeatureProperties`
 * while the runtime schema keeps validating them perfectly. Every consumer then
 * needs a cast to read a styling value, which is exactly the hole the numbered
 * slots existed to close.
 */
function styleSlotShape(): { [K in StyleSlotKey]: z.ZodOptional<typeof styleValueSchema> } {
  return Object.fromEntries(STYLE_SLOT_KEYS.map((key) => [key, styleValueSchema.optional()])) as {
    [K in StyleSlotKey]: z.ZodOptional<typeof styleValueSchema>;
  };
}

export const mapFeatureSchema = z.object({
  type: z.literal('Feature'),
  /** Repeated at the top level because a map library keys features by `id`, not
   *  by anything inside `properties`. */
  id: z.string().uuid(),
  geometry: mapGeometrySchema,
  bbox: boundingBoxSchema,
  properties: mapFeaturePropertiesSchema,
});
export type MapFeature = z.infer<typeof mapFeatureSchema>;

export const mapFeatureCollectionSchema = z.object({
  type: z.literal('FeatureCollection'),
  features: z.array(mapFeatureSchema),
  /** Envelope of everything returned, or null for an empty collection. Saves
   *  the client a second pass to work out where to point the camera. */
  bbox: boundingBoxSchema.nullable(),
  /**
   * True when the result was cut off at `limit`. The map says so rather than
   * showing a partial picture as if it were the whole one -- a reader who
   * cannot tell the difference will conclude the missing work does not exist.
   */
  truncated: z.boolean(),
  /**
   * Which style field each `v` slot holds, in slot order: `fields[0]` names what
   * is in `v0`.
   *
   * The assignment travels with the data rather than being agreed in advance,
   * which is what makes it impossible for the browser to compile a style against
   * a projection one request out of date. A client that restyles a layer and
   * redraws before the refetch lands reads the old array, finds the reference it
   * wants is not in it, and draws that rule's fallback -- which is a visibly
   * plain map for one frame rather than a map coloured by the wrong column.
   */
  fields: z.array(z.string()),
});
export type MapFeatureCollection = z.infer<typeof mapFeatureCollectionSchema>;

/**
 * One feature in full, fetched when a reader selects it.
 *
 * Carries what the collection deliberately leaves out: the description, the
 * custom attributes, the audit fields, and the resolved subject. `attributes`
 * is a flat string map because it is rendered as a definition list -- a nested
 * document would need a JSON viewer, which is a developer tool rather than a
 * project one.
 */
export const mapFeatureDetailSchema = z.object({
  id: z.string().uuid(),
  key: resourceKeySchema,
  name: z.string(),
  description: z.string().nullable(),
  layer: mapLayerKeySchema,
  geometryKind: mapGeometryKindSchema,
  geometry: mapGeometrySchema,
  bbox: boundingBoxSchema,
  centroid: positionSchema,
  statusTone: mapStatusToneSchema,
  statusCode: z.string().nullable(),
  subject: z
    .object({
      kind: mapSubjectKindSchema,
      id: z.string().uuid(),
      label: z.string(),
      /** Where the rest of the application talks about this thing. Built by the
       *  API so the map does not have to know how other modules route. */
      href: z.string(),
    })
    .nullable(),
  attributes: z.record(z.string(), z.string()),
  updatedAt: timestampSchema,
  version: z.number().int().nonnegative(),
});
export type MapFeatureDetail = z.infer<typeof mapFeatureDetailSchema>;

/**
 * One row of the layer legend: what it is, how many there are, and whether any
 * of them need attention. Drives the toggle list and its counts.
 */
/**
 * One class of a layer's colour rule, with how many features are in it.
 *
 * A `category` rule's stops and a `scale` rule's bands become these, and so does
 * the fallback -- which is why `value` is nullable: the last class of every
 * classified layer is "no value", and it is always present and always counted.
 * A null styling field is the first cause of "my map went grey", and a legend
 * row that names it turns a support ticket into a fact.
 */
export const mapLayerClassSchema = z.object({
  /** The stop's value, or null for the fallback class. */
  value: z.string().nullable(),
  /**
   * How many of the project's features fall in this class, or null when the
   * count was not computed.
   *
   * Null is not zero, and the legend must not draw it as zero. Counts are
   * omitted for `attribute:` fields on purpose: the overview query is unbounded
   * by design and deliberately narrow, and reading a TOASTed JSON column for
   * every feature in the project to fill in a legend would undo that
   * (docs/map-layers-design.md section 4).
   */
  count: z.number().int().nonnegative().nullable(),
  /**
   * Translation key for the class name, or null to show `value` as it stands.
   *
   * Resolved by the field registry, which is the only thing that knows a
   * workflow status is called `schedule.workflowStatus.blocked`. Null for a
   * class whose value is the client's own data -- an attribute value, an asset
   * type key -- which is prose nobody translates and which reads correctly as
   * it is.
   *
   * Sent rather than guessed at in the browser: the alternative is asking the
   * translator for a key and showing the raw value when it answers with the key
   * back, which silently mislabels anything whose value happens to collide with
   * a key that exists.
   */
  labelCode: z.string().nullable(),
});
export type MapLayerClass = z.infer<typeof mapLayerClassSchema>;

export const mapLayerSummarySchema = z.object({
  layer: mapLayerKeySchema,
  featureCount: z.number().int().nonnegative(),
  /** Features in this layer whose tone is `warning` or `danger`. A badge showing
   *  "2" is what makes an operator open the layer they had collapsed. */
  attentionCount: z.number().int().nonnegative(),
  /**
   * Which geometry kinds this layer actually holds.
   *
   * The renderer emits only the sublayers a layer needs, so a catalog of twelve
   * layers costs about twenty MapLibre layers rather than seventy-two. It is an
   * observation about the data rather than a property of the layer: a layer that
   * has only ever been drawn as areas grows a point sublayer the moment somebody
   * drops a pin in it.
   */
  geometryKinds: z.array(mapGeometryKindSchema),
  /** The classes of this layer's point colour rule, when it has any. Empty for
   *  a layer coloured by a fixed token or by status. */
  classes: z.array(mapLayerClassSchema),
});
export type MapLayerSummary = z.infer<typeof mapLayerSummarySchema>;

export const mapOverviewSchema = z.object({
  projectId: z.string().uuid(),
  layers: z.array(mapLayerSummarySchema),
  /** Envelope of every feature in the project, or null when there are none.
   *  The map's opening camera, so a first visit is never mid-ocean. */
  bbox: boundingBoxSchema.nullable(),
  featureCount: z.number().int().nonnegative(),
  /**
   * Where this project's features should be fetched from.
   *
   * Decided by the server from the project's own size, and obeyed by the
   * browser. The threshold is a property of what this API is willing to send in
   * one response, so it lives here: a client that worked it out for itself
   * would be a second copy of the number, and the two would drift.
   */
  source: mapSourceModeSchema,
});
export type MapOverview = z.infer<typeof mapOverviewSchema>;

/* ------------------------------------------------------------ wire: search */

/**
 * A search hit.
 *
 * Deliberately not a MapFeature: results are a list, and a list needs a label
 * and somewhere to fly to, not geometry the map already holds. Sending the
 * geometry again would double the payload of the one interaction that has to
 * feel instant.
 */
export const mapSearchHitSchema = z.object({
  id: z.string().uuid(),
  key: resourceKeySchema,
  name: z.string(),
  layer: mapLayerKeySchema,
  statusTone: mapStatusToneSchema,
  centroid: positionSchema,
  bbox: boundingBoxSchema,
  /** Which field matched, so the list can say *why* a hit is a hit when the name
   *  alone does not show it -- a match on a linked service, say. */
  matchedOn: z.enum(['name', 'key', 'description', 'subject']),
});
export type MapSearchHit = z.infer<typeof mapSearchHitSchema>;

export const mapSearchResultSchema = z.object({
  query: z.string(),
  hits: z.array(mapSearchHitSchema),
  truncated: z.boolean(),
});
export type MapSearchResult = z.infer<typeof mapSearchResultSchema>;

/* ----------------------------------------------------------------- queries */

/**
 * Parses the `bbox` query parameter: west,south,east,north.
 *
 * A single string rather than four parameters, because that is the form every
 * mapping client already produces and because four independent parameters can
 * be partially supplied -- three of them is not a smaller filter, it is a bug
 * with no error message.
 */
export const bboxQuerySchema = z
  .string()
  .transform((value, context) => {
    const parts = value.split(',').map((part) => Number(part.trim()));
    if (parts.length !== 4 || parts.some((part) => !Number.isFinite(part))) {
      context.addIssue({ code: 'custom', message: 'expected west,south,east,north' });
      return z.NEVER;
    }
    return parts as [number, number, number, number];
  })
  .pipe(boundingBoxSchema);

export const MAP_FEATURE_LIMIT_DEFAULT = 500;
export const MAP_FEATURE_LIMIT_MAX = 2_000;

export const mapFeatureListQuerySchema = z
  .object({
    bbox: bboxQuerySchema.optional(),
    /** Repeatable: `?layer=site&layer=hazard`. Absent means every layer. */
    layer: z
      .union([mapLayerKeySchema, z.array(mapLayerKeySchema)])
      .transform((value) => (Array.isArray(value) ? value : [value]))
      .optional(),
    subjectKind: mapSubjectKindSchema.optional(),
    q: z.string().max(120).optional(),
    limit: z.coerce
      .number()
      .int()
      .min(1)
      .max(MAP_FEATURE_LIMIT_MAX)
      .default(MAP_FEATURE_LIMIT_DEFAULT),
  })
  .strict();
export type MapFeatureListQuery = z.infer<typeof mapFeatureListQuerySchema>;

export const MAP_SEARCH_LIMIT_MAX = 25;

export const mapSearchQuerySchema = z
  .object({
    q: z.string().min(1).max(120),
    limit: z.coerce.number().int().min(1).max(MAP_SEARCH_LIMIT_MAX).default(10),
  })
  .strict();
export type MapSearchQuery = z.infer<typeof mapSearchQuerySchema>;

/* ---------------------------------------------------------------- mutation */

/**
 * Attribute maps are bounded on both axes.
 *
 * `properties` on a GeoJSON feature is conventionally open, and an open
 * key/value column on a multi-tenant table is a place to put a megabyte. Twenty
 * short entries is more than a definition list can usefully show anyway.
 */
export const MAX_FEATURE_ATTRIBUTES = 20;

export const mapAttributesSchema = z
  .record(z.string().min(1).max(64), z.string().max(500))
  .refine((value) => Object.keys(value).length <= MAX_FEATURE_ATTRIBUTES, {
    message: `at most ${MAX_FEATURE_ATTRIBUTES} attributes`,
  });

const mapFeatureWritableSchema = z.object({
  key: resourceKeySchema,
  name: z.string().min(1).max(200),
  description: z.string().max(2000).nullable().optional(),
  layer: mapLayerKeySchema,
  geometry: mapGeometrySchema,
  attributes: mapAttributesSchema.optional(),
  /**
   * The domain row this feature locates, if any. Both members or neither: a
   * kind with no id links to nothing, and an id with no kind is an identifier
   * nobody can resolve.
   */
  subjectKind: mapSubjectKindSchema.nullable().optional(),
  subjectId: z.string().uuid().nullable().optional(),
});

function subjectIsCoherent(value: {
  subjectKind?: MapSubjectKind | null;
  subjectId?: string | null;
}): boolean {
  return (value.subjectKind == null) === (value.subjectId == null);
}

/** Not `as const`: Zod's refinement options want a mutable `path`. */
const SUBJECT_INCOHERENT = {
  message: 'subjectKind and subjectId must be given together',
  path: ['subjectId'] as PropertyKey[],
};

export const createMapFeatureSchema = mapFeatureWritableSchema
  .strict()
  .refine(subjectIsCoherent, SUBJECT_INCOHERENT);
export type CreateMapFeature = z.infer<typeof createMapFeatureSchema>;

/**
 * Every writable member optional, plus the version last read. Optimistic
 * concurrency is not decoration here: two planners redrawing the same possession
 * boundary is the ordinary case rather than the exotic one.
 */
export const updateMapFeatureSchema = mapFeatureWritableSchema
  .partial()
  .extend(versionedSchema.shape)
  .strict()
  .refine(subjectIsCoherent, SUBJECT_INCOHERENT);
export type UpdateMapFeature = z.infer<typeof updateMapFeatureSchema>;

/* ----------------------------------------------------------------- basemap */

/**
 * What the frontend needs to know before it can draw anything.
 *
 * `styleUrl` is same-origin and always present, because the API answers it
 * itself even when no tile server is configured -- see the basemap service. The
 * client therefore has no "is there a basemap?" branch; it has a background
 * that is either detailed or plain, and the overlay works identically on both.
 */
/**
 * The style's raster source, when there is one.
 *
 * Named in one place both sides import, because the client addresses the source
 * by id to swap its tiles and a mismatch would be a silent no-op: the theme
 * would change, the basemap would not, and nothing would report an error.
 */
export const BASEMAP_RASTER_SOURCE_ID = 'basemap';

/**
 * The style's raster layer, named here for the same reason as the source above.
 *
 * The client repaints this layer when the colour mode changes and there is no
 * dark upstream to swap to. Getting the id wrong would be silent in exactly the
 * same way: the theme would change, the background would not, and nothing would
 * report an error.
 */
export const BASEMAP_RASTER_LAYER_ID = 'basemap';

export const basemapDescriptorSchema = z.object({
  styleUrl: z.string(),
  /** False when the style is the built-in blank canvas. Surfaced in the UI as a
   *  quiet note, so an operator seeing an empty background knows why. */
  detailed: z.boolean(),
  /** Shown in the map corner. Attribution is a licence condition of every open
   *  basemap worth using, so it is data the API supplies rather than a string
   *  the frontend might forget. */
  attribution: z.string(),
  /** Camera to use when a project has no features to fit to yet. */
  defaultCenter: positionSchema,
  defaultZoom: z.number().min(0).max(22),
  /**
   * Tile URLs per colour mode, for a background that has a dark counterpart.
   *
   * Null whenever the background does not change with the theme -- a vector
   * style the client restyles itself, a raster basemap with no dark variant
   * configured, or no basemap at all. The client swaps these onto the named
   * source rather than reloading the style, because reloading a style discards
   * every layer added to it and the whole project overlay is added to it.
   */
  themedTiles: z
    .object({
      sourceId: z.string(),
      light: z.string(),
      dark: z.string(),
    })
    .nullable(),
  /**
   * Paint adjustments for the raster layer, per colour mode.
   *
   * Present only for a raster background that has no dark tiles of its own --
   * the National Land Survey's open tiles, and every other upstream that
   * publishes a single rendering. There is no second set of tiles to swap to,
   * so the alternative to a white map under a dark interface is to turn the one
   * that exists down. Null everywhere else, including when `themedTiles` is
   * set: real dark cartography is a different upstream, not the same one dimmed.
   *
   * A map of MapLibre paint property names to values rather than a fixed shape,
   * because which properties do the job is a cartographic decision belonging to
   * whoever configured the background. The client's part is to apply them to
   * the named layer and to put them back on the way out, which is why the light
   * half is carried explicitly instead of being left as "the defaults".
   */
  themedRaster: z
    .object({
      layerId: z.string(),
      light: z.record(z.string(), z.number()),
      dark: z.record(z.string(), z.number()),
    })
    .nullable(),
});
export type BasemapDescriptor = z.infer<typeof basemapDescriptorSchema>;

/**
 * Which project's camera the descriptor should answer with.
 *
 * Optional, because the basemap itself is deployment configuration and every
 * other field is the same for every project. Only the opening view is
 * project-scoped, and resolving it here rather than in a second request keeps
 * one answer to "where does the map open" -- a client merging two queries would
 * have two loading states and a frame of the wrong camera between them.
 */
export const basemapDescriptorQuerySchema = z
  .object({ projectId: z.string().uuid().optional() })
  .strict();
export type BasemapDescriptorQuery = z.infer<typeof basemapDescriptorQuerySchema>;

/* --------------------------------------------------- project map settings */

/**
 * A camera: where a map opens, and how far in.
 *
 * The same three numbers `MAP_DEFAULT_VIEW` carries in the environment, given a
 * shape here because they now travel together over the wire and are stored
 * together. Zoom is bounded by what MapLibre accepts rather than by what any
 * particular basemap has tiles for -- an upstream's `maxZoom` clamps rendering,
 * and a default view past it is a view that works, slightly blurred, rather
 * than a validation error an administrator cannot act on.
 */
export const mapDefaultViewSchema = z.object({
  center: positionSchema,
  zoom: z.number().min(0).max(22),
});
export type MapDefaultView = z.infer<typeof mapDefaultViewSchema>;

/**
 * A project's map settings.
 *
 * The first DB-backed piece of what used to be environment-only map
 * configuration, and the shape every later one should copy:
 *
 * **`defaultView` is nullable, and null means inherit.** Not "unset, so use
 * zero" -- explicitly deferring to `MAP_DEFAULT_VIEW`. That is what makes the
 * move from environment to database a widening rather than a migration: a
 * deployment that never opens this section behaves exactly as it did, and an
 * administrator who gets it wrong has a way back that does not involve a shell.
 *
 * **`inherited` is sent alongside it**, so the editor can show what "inherit"
 * currently resolves to instead of an empty field the administrator has to
 * guess at. Read-only, and not accepted on write: it is the environment's
 * value, and the environment is not editable from a browser.
 *
 * The override is atomic -- a centre without a zoom is not a camera -- so this
 * is one nullable object rather than three nullable numbers. A database check
 * constraint says the same thing about the columns underneath.
 */
export const projectMapSettingsSchema = z
  .object({
    id: z.string().uuid(),
    projectId: z.string().uuid(),
    defaultView: mapDefaultViewSchema.nullable(),
    inherited: mapDefaultViewSchema,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .merge(versionedSchema);
export type ProjectMapSettings = z.infer<typeof projectMapSettingsSchema>;

export const updateProjectMapSettingsSchema = z
  .object({
    /**
     * Required rather than optional, unlike the partial updates elsewhere in
     * this file. Null is a meaningful value here -- "go back to inheriting" --
     * and an optional nullable field cannot distinguish it from "leave alone"
     * once it has crossed JSON.
     */
    defaultView: mapDefaultViewSchema.nullable(),
    version: z.number().int().nonnegative(),
  })
  .strict();
export type UpdateProjectMapSettings = z.infer<typeof updateProjectMapSettingsSchema>;

/* ------------------------------------------------------------------ tiles */

/**
 * The count above which a project is served as tiles.
 *
 * Set at the collection endpoint's own default page, not at its maximum: the
 * point of switching is to stop sending pages, and a project that needs two
 * requests already needs tiles more than it needs a bigger page.
 */
export const MAP_TILE_THRESHOLD = MAP_FEATURE_LIMIT_DEFAULT;

/**
 * The ceiling on what one project may be tiled from.
 *
 * Tiles are built in the API from the stored geometry rather than by the
 * database, so the whole project is held in memory while its index is built.
 * That is comfortable into the tens of thousands and dishonest past it -- which
 * is the point at which a deployment genuinely wants PostGIS and `ST_AsMVT`,
 * and the service says so rather than quietly dropping the tail.
 */
export const MAX_TILED_FEATURES = 50_000;

/**
 * The two layers inside every tile.
 *
 * The same split the GeoJSON path makes into two sources, for the same reason:
 * a fill layer and a circle layer want different things from the data they
 * read, and separating them at the boundary means each layer's filter is a
 * formality rather than the thing keeping it correct.
 *
 * Named in one place both sides import, because the client addresses a vector
 * source's contents by `source-layer` and a mismatch is a silent no-op: the
 * tiles arrive, the layers draw nothing, and nothing reports an error.
 */
export const MAP_TILE_LAYERS = { points: 'points', shapes: 'shapes' } as const;
export type MapTileLayer = (typeof MAP_TILE_LAYERS)[keyof typeof MAP_TILE_LAYERS];

/**
 * The tile extent, in tile-local units.
 *
 * 4096 is the vector tile specification's own convention and what every
 * renderer assumes when a tile does not say otherwise. It is named here because
 * the builder and the TileJSON have to agree, not because anybody should change
 * it.
 */
export const MAP_TILE_EXTENT = 4096;

/**
 * How far in tiles preserve full detail.
 *
 * Past this the renderer over-zooms the last tile rather than the server
 * cutting more of them, which is what every vector basemap does: the geometry
 * is already at full precision by then, so further tiles would carry the same
 * coordinates in smaller boxes.
 */
export const MAP_TILE_MAX_ZOOM = 16;

/**
 * TileJSON, plus the one thing TileJSON has no room for.
 *
 * `fields` is the slot assignment the GeoJSON path sends in its collection
 * envelope. It has to travel with the tiles for exactly the same reason -- the
 * browser resolves `['get', 'v0']` by looking a reference up in it -- and this
 * document is where the tiles are described, so this is where it belongs. A
 * client that fetched tiles without it would compile every classified rule
 * against a projection it could not name.
 */
export const mapTilesDescriptorSchema = z
  .object({
    tilejson: z.literal('3.0.0'),
    projectId: z.string().uuid(),
    /** URL templates, `{z}/{x}/{y}`. Same-origin and behind the session. */
    tiles: z.array(z.string()).min(1),
    minzoom: z.number().int().min(0).max(24),
    maxzoom: z.number().int().min(0).max(24),
    /** The project's footprint, as TileJSON writes it: west, south, east,
     *  north. Null for a project with nothing in it yet. */
    bounds: boundingBoxSchema.nullable(),
    fields: z.array(z.string()),
    /** What the tiles were built from, so the interface can say how much is on
     *  the map without a second request. */
    featureCount: z.number().int().nonnegative(),
    /**
     * Changes whenever the tiles would. Carried in the tile URLs, so a reader
     * whose browser cached yesterday's tiles asks for today's rather than
     * being served a map of what the project used to be.
     */
    version: z.string(),
  })
  .strict();
export type MapTilesDescriptor = z.infer<typeof mapTilesDescriptorSchema>;

export const mapTileParamsSchema = z
  .object({
    z: z.coerce.number().int().min(0).max(24),
    x: z.coerce.number().int().min(0),
    y: z.coerce.number().int().min(0),
  })
  .strict()
  /*
   * A tile coordinate has to be inside its own zoom level. Without this a
   * request for `0/9999/9999` is a perfectly well-typed way to ask the index
   * for a tile that cannot exist, and the honest answer is a 400 rather than an
   * empty tile that looks like missing data.
   */
  .refine((value) => value.x < 2 ** value.z && value.y < 2 ** value.z, {
    message: 'tile coordinates must lie within the zoom level',
  });
export type MapTileParams = z.infer<typeof mapTileParamsSchema>;

/* ------------------------------------------------------------ the catalog */

/**
 * A layer, as a row somebody owns.
 *
 * Organization-scoped, the shape `InfrastructureAssetType` established: the
 * vocabulary a client uses for what goes on a map belongs to the client, not to
 * one job. Per-project overrides are deliberately not here -- they are a second
 * table, a merge rule and a "why is this different on this project?" question,
 * and nobody has asked for one yet (docs/map-layers-design.md section 2.1).
 */
export const mapLayerSchema = z
  .object({
    id: z.string().uuid(),
    key: mapLayerKeySchema,
    name: z.string().min(1).max(120),
    description: z.string().max(500).nullable(),
    /**
     * The translation key this layer's name is read through, or null.
     *
     * Set on the five layers the deployment seeds, so they keep the translated
     * names they have always had; null on anything somebody made, and cleared
     * the moment somebody renames a seeded one -- a translation that no longer
     * describes the row is worse than no translation.
     */
    builtinKey: z.string().max(32).nullable(),
    /** Draw order, low to high. The reader's order wins: a translucent area in a
     *  high layer draws over a pin in a low one (design section 5). */
    orderIndex: z.number().int().nonnegative(),
    /** Off by default on a fresh map. A layer of survey control points is worth
     *  having and not worth seeing every time somebody opens the project. */
    hiddenByDefault: z.boolean(),
    /**
     * The tone a feature in this layer takes when it is linked to nothing.
     *
     * This was a hard-coded table keyed by the enum -- `hazard` was `warning`
     * and everything else `neutral`. It is a column now, because "what does a
     * feature of this kind mean when there is nothing to ask?" is exactly the
     * sort of thing the client who invented the kind should answer.
     */
    fallbackTone: mapStatusToneSchema,
    style: mapLayerStyleSchema,
    /** How many features across the whole organization are in this layer. Read
     *  by the delete confirmation, which must say what it is about to block. */
    featureCount: z.number().int().nonnegative(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .merge(versionedSchema);
export type MapLayer = z.infer<typeof mapLayerSchema>;

const mapLayerWritableSchema = z.object({
  key: mapLayerKeySchema,
  name: z.string().min(1).max(120),
  description: z.string().max(500).nullable().optional(),
  hiddenByDefault: z.boolean().optional(),
  fallbackTone: mapStatusToneSchema.optional(),
  style: mapLayerStyleSchema.optional(),
});

export const createMapLayerSchema = mapLayerWritableSchema.strict();
export type CreateMapLayer = z.infer<typeof createMapLayerSchema>;

/**
 * `key` is absent from the update, deliberately.
 *
 * A feature's properties carry the key into every style filter, every saved tile
 * configuration and every import file, and renaming it would silently detach all
 * of them. The display name is what people actually want to change, and that is
 * free.
 */
export const updateMapLayerSchema = mapLayerWritableSchema
  .omit({ key: true })
  .partial()
  .extend(versionedSchema.shape)
  .strict();
export type UpdateMapLayer = z.infer<typeof updateMapLayerSchema>;

/** The whole order in one call, rather than a move per layer: a drag produces
 *  one new arrangement, and sending it whole is what makes it atomic. */
export const MAX_MAP_LAYERS = 40;

export const reorderMapLayersSchema = z
  .object({ layerIds: z.array(z.string().uuid()).min(1).max(MAX_MAP_LAYERS) })
  .strict();
export type ReorderMapLayers = z.infer<typeof reorderMapLayersSchema>;

/**
 * Deleting a layer that still holds features.
 *
 * Refused unless the caller says where the features should go. A cascade would
 * delete somebody's survey, and an orphan column is not available -- a feature
 * with no layer cannot be drawn, filtered or found.
 */
export const deleteMapLayerQuerySchema = z
  .object({ reassignTo: z.string().uuid().optional() })
  .strict();
export type DeleteMapLayerQuery = z.infer<typeof deleteMapLayerQuerySchema>;

/** Re-exported so a consumer importing the map contract gets the style
 *  vocabulary with it, rather than having to know it lives next door. */
export type { MapLayerStyle };

/* ------------------------------------------------- deployment basemap */

/**
 * Hosts an operator must not be able to point the tile proxy at.
 *
 * Configuring the basemap used to require shell access to the deployment, so
 * the URL was trusted by construction. Editing it from a settings form is a
 * different proposition: the API fetches these URLs itself, so whoever can set
 * one can make this server issue a request and read the reply -- which is
 * server-side request forgery with a permission attached to it.
 *
 * Private ranges are deliberately *not* blocked, because the self-hosted vector
 * tile server this product recommends runs at `http://localhost:8080` or on the
 * deployment's own network, and refusing that would refuse the best-configured
 * case. What is blocked is the cloud instance metadata service, which is the
 * one internal endpoint that is both universally present and worth stealing:
 * on every major provider it hands out role credentials to anything that can
 * make a plain HTTP request to it.
 *
 * This narrows the risk rather than removing it. An administrator can still
 * aim the proxy at an internal service, and a deployment that treats that as
 * unacceptable should keep this section to provider-level operators.
 */
const BLOCKED_BASEMAP_HOSTS = new Set([
  '169.254.169.254',
  '[fd00:ec2::254]',
  'fd00:ec2::254',
  'metadata.google.internal',
  'metadata.goog',
]);

/**
 * A URL the API may be told to fetch.
 *
 * `http` is allowed alongside `https` for the self-hosted case above. Userinfo
 * is refused: a proxied URL carrying `user:password@` puts a credential
 * somewhere every redirect and error path would have to remember to strip, and
 * every basemap provider worth using takes its key as a query parameter.
 */
export const proxiedUrlSchema = z
  .string()
  .min(1)
  .max(2048)
  .superRefine((value, context) => {
    let url: URL;
    try {
      url = new URL(value);
    } catch {
      context.addIssue({ code: z.ZodIssueCode.custom, message: 'expected an absolute URL' });
      return;
    }

    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      context.addIssue({ code: z.ZodIssueCode.custom, message: 'expected an http or https URL' });
    }
    if (url.username !== '' || url.password !== '') {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'a username or password in the URL is not accepted',
      });
    }
    if (BLOCKED_BASEMAP_HOSTS.has(url.hostname.toLowerCase())) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'that host is the cloud instance metadata service',
      });
    }
  });

/**
 * The path (and optional query) a vector style JSON lives at, relative to a
 * `vector` source's `url`.
 *
 * Every provider this product has been pointed at uses a different convention
 * here -- a self-hosted tileserver-gl wants `/styles/basic/style.json`,
 * OpenFreeMap wants `/styles/liberty` with no suffix at all, and the National
 * Land Survey's wants a versioned path plus a `TileMatrixSet` query parameter.
 * Storing the whole thing as the operator's problem, rather than this package
 * guessing a shared shape, is what makes all three work with the same code.
 *
 * It is validated as a *path*, not a URL: no scheme, no authority, nothing
 * that could resolve against a different host than the one `url` names. `url`
 * is where the SSRF allowlist in `proxiedUrlSchema` is enforced, and a style
 * path that could smuggle in `//other-host/...` or `https://other-host/...`
 * would be a second way to reach a host that check was supposed to be the only
 * gate for.
 */
export const vectorStylePathSchema = z
  .string()
  .min(1)
  .max(512)
  .superRefine((value, context) => {
    if (!value.startsWith('/')) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'expected a path starting with /, resolved against the source URL',
      });
      return;
    }
    // `new URL(value, 'http://x')` normalises `//host/...` into an absolute
    // URL whose host is not `x` -- which is exactly the protocol-relative
    // escape this field must not allow. Resolving against a fixed base and
    // checking the host came back unchanged catches that the same way
    // `sanitizePath` does on the server, without duplicating its string logic.
    const resolved = new URL(value, 'http://vector-style.invalid');
    if (resolved.host !== 'vector-style.invalid' || value.includes('..')) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'expected a path on the source URL, not a link elsewhere',
      });
    }
  });

/**
 * The query parameter name a keyed vector provider expects its API key under,
 * e.g. `api-key`. Restricted to an identifier-like shape because it is
 * interpolated into a query string the server builds server-side: anything
 * containing `=`, `&` or `#` could inject a second parameter rather than name
 * one.
 */
export const apiKeyParamNameSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[A-Za-z0-9_-]+$/, 'expected a plain parameter name');

/**
 * A raster tile template, which is a URL with three placeholders in it.
 *
 * Validated as a URL after the placeholders are substituted rather than before:
 * `{z}` is legal in a URL path but the string as written is not what gets
 * fetched, and checking the literal would pass a template whose substituted
 * form is something else entirely.
 *
 * Substituting one fixed value is not enough on its own, though, because a
 * template is not one URL but every URL it can produce. `http://169.254.169.25{z}`
 * is an ordinary host at most zoom levels and the instance metadata service at
 * zoom 4 -- so any single probe passes it. Rather than guess at coordinates,
 * the placeholders are confined to the path and query: with a literal host,
 * checking the host once is checking it for every tile.
 */
export const xyzTemplateSchema = z
  .string()
  .min(1)
  .max(2048)
  .superRefine((value, context) => {
    for (const token of ['{z}', '{x}', '{y}']) {
      if (!value.includes(token)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: `expected an XYZ template containing ${token}`,
        });
      }
    }

    const authorityEnd = value.indexOf('://') + 3;
    const authority =
      authorityEnd < 3 ? '' : (value.slice(authorityEnd).split(/[/?#]/, 1)[0] ?? '');
    if (authority.includes('{')) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'the host must be written out; placeholders belong in the path or query',
      });
      return;
    }

    const substituted = value.replace('{z}', '0').replace('{x}', '0').replace('{y}', '0');
    const asUrl = proxiedUrlSchema.safeParse(substituted);
    if (!asUrl.success) {
      for (const issue of asUrl.error.issues) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: issue.message });
      }
    }
  });

/**
 * What the background is made of, as a closed set rather than a handful of
 * nullable fields.
 *
 * Three independent settings could otherwise describe a deployment that is both
 * self-hosted and on a CDN, and every reader would have to re-derive the
 * precedence rule. Here there is one answer and no rule to remember -- which is
 * the shape the environment-based configuration resolved *to*, now that it is
 * the shape stored and edited as well.
 */
export const basemapSourceSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('none') }).strict(),
  z
    .object({
      kind: z.literal('vector'),
      /** Origin every other resource the style refers to is expected to
       *  share -- a self-hosted tileserver-gl, or a third-party host such as
       *  OpenFreeMap's or the National Land Survey's. No trailing slash is
       *  kept. */
      url: proxiedUrlSchema,
      /** Where the style JSON lives, relative to `url`. */
      stylePath: vectorStylePathSchema,
      /** Query parameter a keyed provider expects its API key under, or null
       *  for one that needs none -- OpenFreeMap, or a self-hosted server. */
      apiKeyParam: apiKeyParamNameSchema.nullable(),
      /** The key itself, or null. Both members or neither: a parameter name
       *  with no value has nothing to send, and a value with no parameter
       *  name is a secret with nowhere to go. Never sent to the browser --
       *  see `BasemapService.vectorStyle` for where that is enforced. */
      apiKey: z.string().min(1).max(256).nullable(),
    })
    .strict()
    .refine((value) => (value.apiKeyParam == null) === (value.apiKey == null), {
      message: 'apiKeyParam and apiKey must be given together',
      path: ['apiKey'],
    }),
  z
    .object({
      kind: z.literal('raster'),
      /** XYZ template. Never handed to the browser: tiles are proxied. */
      light: xyzTemplateSchema,
      /** Null when the operator configured no dark counterpart. */
      dark: xyzTemplateSchema.nullable(),
      tileSize: z.number().int().min(64).max(1024),
      maxZoom: z.number().int().min(1).max(24),
    })
    .strict(),
]);
export type BasemapSource = z.infer<typeof basemapSourceSchema>;

/**
 * The deployment's background map, and where its maps open by default.
 *
 * One row per deployment, because a basemap is bought, licensed and pointed at
 * an upstream once -- not per project. The camera lives here too rather than
 * beside it in the environment: it is the value a project's own opening view
 * falls back to, and a fallback that lives somewhere the administrator cannot
 * see is a fallback they cannot reason about.
 *
 * Attribution stays deployment-wide with the source it credits. It is a licence
 * condition of whichever upstream is configured, so a per-project value would
 * let two projects credit different sources for identical tiles.
 */
export const basemapSettingsSchema = z
  .object({
    id: z.string().uuid(),
    source: basemapSourceSchema,
    attribution: z.string().max(500),
    defaultView: mapDefaultViewSchema,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .merge(versionedSchema);
export type BasemapSettings = z.infer<typeof basemapSettingsSchema>;

export const updateBasemapSettingsSchema = z
  .object({
    source: basemapSourceSchema,
    attribution: z.string().max(500),
    defaultView: mapDefaultViewSchema,
    version: z.number().int().nonnegative(),
  })
  .strict();
export type UpdateBasemapSettings = z.infer<typeof updateBasemapSettingsSchema>;

/** A deployment with nothing configured draws the graticule the API generates,
 *  which is a supported background rather than a broken one. */
export const DEFAULT_BASEMAP_VIEW: MapDefaultView = { center: [24.94, 60.28], zoom: 8 };

/**
 * What a deployment draws before an administrator has opened this settings
 * section, and what a fresh row is materialized with.
 *
 * OpenFreeMap's `liberty` style, rather than `none`. That is a deliberate
 * reversal of this feature's original default -- see `docs/decisions.md`,
 * "Three backgrounds" -- made once a *third-party vector* source existed that
 * needed no key and no container: OpenFreeMap is free, requires no account,
 * and is proxied same-origin like every other configured source (the
 * deployment's own address reaches OpenFreeMap tile by tile, exactly as
 * described for a raster CDN; the reader's never does). A deployment that
 * finds even that unacceptable sets the source to `none` explicitly, which
 * remains supported and draws the graticule instead.
 */
export const DEFAULT_BASEMAP_SOURCE: BasemapSource = {
  kind: 'vector',
  url: 'https://tiles.openfreemap.org',
  stylePath: '/styles/liberty',
  apiKeyParam: null,
  apiKey: null,
};

/** OpenFreeMap's own required credit line -- see https://openfreemap.org. */
export const DEFAULT_BASEMAP_ATTRIBUTION = 'OpenFreeMap © OpenMapTiles Data from OpenStreetMap';
